import { StrictMode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import { Provider } from 'mobx-react';

import authStore from 'stores/authStore';
import registrationStore from 'stores/registrationStore';
import emailRegistrationStore from 'stores/emailRegistrationStore';
import userStore from 'stores/userStore';
import passwordRecoveryStore from 'stores/passwordRecoveryStore';
import paymentStore from 'stores/paymentStore';
import newPaymentStore from 'stores/newPaymentStore';
import transactionsStore from 'stores/transactionsStore';
import betweenAccountsStore from 'stores/betweenAccountsStore';
import currencyExchangeStore from 'stores/currencyExchangeStore';
import messagesStore from 'stores/messagesStore';
import activityLogStore from 'stores/activityLogStore';
import representativeStore from 'stores/representativeStore';
import cardsStore from 'stores/cardsStore';
import paymentObligationsStore from 'stores/paymentObligationsStore';
import documentRequestStore from 'stores/documentRequestStore';
import App from 'components/App';
import { setImRef } from 'components/utils';
import { getAccessToken } from 'services/authUtils';
import { UNAUTHORIZED_ROUTE_PATHS } from 'routes/constants';

import 'assets/fonts/Manrope-Regular.ttf';
import 'assets/fonts/Manrope-SemiBold.ttf';
import 'bootstrap/dist/css/bootstrap.css';
import 'styles.scss';

const stores = {
  authStore,
  registrationStore,
  emailRegistrationStore,
  userStore,
  passwordRecoveryStore,
  paymentStore,
  newPaymentStore,
  transactionsStore,
  betweenAccountsStore,
  currencyExchangeStore,
  messagesStore,
  activityLogStore,
  representativeStore,
  cardsStore,
  paymentObligationsStore,
  documentRequestStore
};

// Capture the referral tracking parameter from the landing URL as early as possible — before any
// React component effect runs — so the first telemetry event on the landing page carries it.
const imRef = new URLSearchParams(window.location.search).get('im_ref');
if (imRef) {
  setImRef(imRef);
}

// TRN-7470: browser Back after a logout can restore an app page from the back/forward cache with its JS state
// frozen as it was (e.g. the "Log out" pop-up still open) although the session behind it is gone. Reload it
// instead, so the app's normal bootstrap routes a visitor without a token to /login. Only a missing token
// counts — an expired access token is still refreshed by the restored page as before — and only on pages
// that need a session: public flows (registration, password recovery, document request) keep their restored
// state, same public-path rule as App.js's own redirect-to-login guard.
window.addEventListener('pageshow', (event) => {
  const { pathname } = window.location;
  const isPublicPage =
    Object.values(UNAUTHORIZED_ROUTE_PATHS).some((path) => pathname.includes(path)) ||
    pathname.includes('/document-request');

  if (event.persisted && !getAccessToken() && !isPublicPage) {
    window.location.reload();
  }
});

const root = createRoot(document.getElementById('root'));
root.render(
  <StrictMode>
    <Provider {...stores}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Provider>
  </StrictMode>
);
